import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, FileText, Sparkles, Trash2, Upload } from 'lucide-react';

// DOKUMENT-ERKENNUNG-PARTNER-001 — Fahrzeug-ENTWURF: erst Dokumente, dann Felder bestätigen.
//
// Ablauf (Owner-Entscheid 03.09.2026):
//   1. Fahrzeugschein + Konzession hochladen → das Backend hält die Datei und liest die Felder.
//   2. Die erkannten Werte stehen im Formular VOR („erkannt"), jedes Feld bleibt frei änderbar.
//      Werte, die der Partner selbst angefasst hat, überschreibt eine spätere Erkennung nie.
//   3. „Bestätigen und zur Freigabe" reicht beide Dateien mit den bestätigten Daten ein und
//      schickt das Fahrzeug in die Admin-Prüfung. Das Portal befüllt nur — freigeben tut der Admin.
// Fail-closed: ohne Erkennung (kein Schlüssel, Anbieterfehler) bleibt alles von Hand ausfüllbar.

export const DRAFT_FIELDS = [
  { key: 'brand',                label: 'Marke *',                        ph: 'Mercedes-Benz' },
  { key: 'model',                label: 'Modell *',                       ph: 'V 250 d' },
  { key: 'year',                 label: 'Baujahr *',                      ph: '2023', hint: 'aus Feld B (Erstzulassung)' },
  { key: 'license_plate',        label: 'Kennzeichen *',                  ph: 'B-UN 1234' },
  { key: 'color',                label: 'Farbe',                          ph: 'Schwarz' },
  { key: 'tuev_date',            label: 'Nächste HU',                     type: 'date' },
  { key: 'seats',                label: 'Sitzplätze (S.1) *',             type: 'number', min: 1, max: 9 },
  { key: 'zulassung_issue_date', label: 'Fahrzeugschein ausgestellt am *', type: 'date', hint: 'Feld I' },
  { key: 'konzession_issue',     label: 'Konzession gültig ab *',         type: 'date' },
  { key: 'konzession_expiry',    label: 'Konzession gültig bis *',        type: 'date' },
  { key: 'permit_number',        label: 'Genehmigungsnummer *',           ph: 'Aktenzeichen / Urkundennummer' },
];
const EMPTY = Object.fromEntries(DRAFT_FIELDS.map((f) => [f.key, '']));

const DOCS = [
  { type: 'zulassung',  label: 'Zulassungsbescheinigung Teil I (Fahrzeugschein)', what: 'Kennzeichen, Marke, Modell, Baujahr, Farbe, Sitze, HU' },
  { type: 'konzession', label: 'Fahrzeug-Konzession (§ 49 PBefG)',                 what: 'gültig ab, gültig bis, Kennzeichen, Genehmigungsnummer' },
];

// Erkennungsstatus eines gehaltenen Uploads in einen Satz übersetzen — ehrlich, nie beschönigt.
export function extractionNotice(hold) {
  if (!hold) return null;
  const ex = hold.extraction || {};
  const n = Object.keys(ex.fields || {}).length;
  if (ex.status === 'ok') return { tone: 'ok', text: n > 0 ? `${n} Feld${n === 1 ? '' : 'er'} erkannt — bitte prüfen` : 'Hochgeladen — nichts sicher lesbar, bitte Felder eintragen' };
  if (ex.status === 'unsupported') return { tone: 'info', text: 'Dateiformat wird nicht automatisch ausgelesen — bitte Felder eintragen' };
  if (ex.status === 'disabled') return { tone: 'info', text: 'Hochgeladen — automatische Erkennung ist abgeschaltet, bitte Felder eintragen' };
  return { tone: 'info', text: 'Hochgeladen — Erkennung derzeit nicht verfügbar, bitte Felder eintragen' };
}

// Vorbefüllung übernehmen, ohne vom Partner angefasste Felder zu überschreiben.
export function applyPrefill(form, prefill, touched) {
  const next = { ...form };
  for (const f of DRAFT_FIELDS) {
    if (touched.has(f.key)) continue;
    const v = prefill && prefill[f.key];
    if (v != null && v !== '') next[f.key] = String(v);
  }
  return next;
}

export default function VehicleDraftWizard({ vehicle, onChanged }) {
  const draft = vehicle.draft || { holds: [], prefill: {}, source: {}, conflicts: [], missing_types: ['zulassung', 'konzession'], ready: false, extraction: { ok: true } };
  const [form, setForm] = useState(() => applyPrefill(EMPTY, draft.prefill, new Set()));
  // Vom Partner angefasste Felder — als Ref, damit der Vorbefüllungs-Effekt immer den aktuellen
  // Stand sieht, ohne selbst von jeder Eingabe neu zu laufen.
  const touchedRef = useRef(new Set());
  const touched = touchedRef.current;
  const [uploading, setUploading] = useState({});
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  // Neue Vorbefüllung vom Server (nach Upload/Neuladen) in unberührte Felder übernehmen.
  const prefill = draft.prefill;
  useEffect(() => { setForm((f) => applyPrefill(f, prefill, touchedRef.current)); }, [prefill]);

  const holdsByType = useMemo(() => Object.fromEntries((draft.holds || []).map((h) => [h.document_type, h])), [draft.holds]);
  const submitted = draft.submitted_types || [];
  const setField = (key, value) => {
    touchedRef.current.add(key);
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => { if (!e[key]) return e; const n = { ...e }; delete n[key]; return n; });
  };

  const upload = async (documentType, e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading((u) => ({ ...u, [documentType]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', documentType);
      const r = await api.post(`/partners/vehicles/${vehicle.id}/documents/recognize`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const notice = extractionNotice(r.data.hold);
      if (notice) (notice.tone === 'ok' ? toast.success : toast)(notice.text);
      if (r.data.draft) setForm((f) => applyPrefill(f, r.data.draft.prefill, touched));
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploading((u) => ({ ...u, [documentType]: false }));
    }
  };

  const confirm = async () => {
    setBusy(true);
    setErrors({});
    try {
      const r = await api.post(`/partners/vehicles/${vehicle.id}/confirm`, form);
      toast.success(`Fahrzeug eingereicht — Klasse: ${r.data.auto_class}${r.data.class_source === 'fallback' ? ' (wird vom Admin geprüft)' : ''}. Es wird jetzt geprüft.`);
      onChanged && onChanged();
    } catch (err) {
      const d = err.response?.data || {};
      if (d.code === 'validation' && d.fields) setErrors(d.fields);
      toast.error(d.error || 'Bestätigung fehlgeschlagen');
      if (d.reupload_required) onChanged && onChanged();
    } finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm('Entwurf und hochgeladene Dateien verwerfen?')) return;
    setBusy(true);
    try { await api.delete(`/partners/vehicles/${vehicle.id}/draft`); toast.success('Entwurf verworfen'); onChanged && onChanged(); }
    catch (err) { toast.error(err.response?.data?.error || 'Fehler'); }
    finally { setBusy(false); }
  };

  const inp = (key) => ({ width: '100%', padding: '10px 12px', border: `1.5px solid ${errors[key] ? '#dc2626' : '#e5e7eb'}`, borderRadius: 8, fontSize: 14, color: '#111', boxSizing: 'border-box' });
  const lbl = { fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 };
  const recognized = (key) => draft.source && draft.source[key] && !touched.has(key) && form[key] !== '';

  return (
    <div data-testid={`vehicle-draft-${vehicle.id}`} style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1.5px solid #9C27B0', gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Neues Fahrzeug <span style={{ fontSize: 12, fontWeight: 700, color: '#9C27B0', background: '#faf5fb', padding: '2px 9px', borderRadius: 20, marginLeft: 6 }}>Entwurf</span></h3>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
            1. Fahrzeugschein und Konzession hochladen · 2. erkannte Felder prüfen und ergänzen · 3. bestätigen — dann prüft das VOOGO-Team.
          </p>
        </div>
        <button type="button" onClick={discard} disabled={busy} data-testid={`draft-discard-${vehicle.id}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
          <Trash2 size={13} /> Entwurf verwerfen
        </button>
      </div>

      {draft.extraction && draft.extraction.ok === false && (
        <div data-testid={`draft-extraction-off-${vehicle.id}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f3f4f6', borderRadius: 8, padding: '8px 10px', margin: '10px 0', fontSize: 12, color: '#4b5563' }}>
          <FileText size={14} style={{ flexShrink: 0, marginTop: 1 }} /> Die automatische Erkennung ist gerade nicht verfügbar. Lade die Dokumente trotzdem hoch und trage die Felder von Hand ein.
        </div>
      )}

      {/* Schritt 1: Dokumente */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, margin: '12px 0 16px' }}>
        {DOCS.map((d) => {
          const hold = holdsByType[d.type];
          const done = submitted.includes(d.type);
          const notice = extractionNotice(hold);
          return (
            <div key={d.type} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, background: hold || done ? '#faf5fb' : '#f9fafb' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{d.label}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Daraus wird gelesen: {d.what}</div>
              {hold && (
                <div data-testid={`draft-hold-${d.type}-${vehicle.id}`} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: notice.tone === 'ok' ? '#9C27B0' : '#4b5563', fontWeight: 600, marginBottom: 8 }}>
                  {notice.tone === 'ok' ? <Sparkles size={13} style={{ flexShrink: 0, marginTop: 1 }} /> : <FileText size={13} style={{ flexShrink: 0, marginTop: 1 }} />}
                  <span>{hold.original_name || 'Datei'} · {notice.text}</span>
                </div>
              )}
              {done && !hold && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#9C27B0', fontWeight: 600, marginBottom: 8 }}><CheckCircle size={13} /> Bereits eingereicht</div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fff', border: '1px dashed #d1d5db', borderRadius: 8, cursor: uploading[d.type] ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                <Upload size={13} /> {uploading[d.type] ? 'Wird gelesen…' : (hold ? 'Andere Datei wählen' : 'Foto oder Scan hochladen')}
                <input data-testid={`draft-upload-${d.type}-${vehicle.id}`} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" capture="environment"
                  disabled={!!uploading[d.type] || busy} onChange={(e) => upload(d.type, e)} style={{ display: 'none' }} />
              </label>
            </div>
          );
        })}
      </div>

      {draft.conflicts && draft.conflicts.length > 0 && (
        <div data-testid={`draft-conflict-${vehicle.id}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
          <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, lineHeight: 1.4 }}>
            {draft.conflicts.map((c) => `Kennzeichen weicht ab: Fahrzeugschein „${c.zulassung}", Konzession „${c.konzession}". Bitte prüfen, welches stimmt.`).join(' ')}
          </span>
        </div>
      )}

      {/* Schritt 2: Felder prüfen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 6 }}>
        {DRAFT_FIELDS.map((f) => (
          <div key={f.key}>
            <label style={lbl}>
              {f.label}
              {recognized(f.key) && <span data-testid={`draft-recognized-${f.key}-${vehicle.id}`} style={{ fontSize: 10, fontWeight: 700, color: '#9C27B0', background: '#faf5fb', padding: '1px 6px', borderRadius: 6, textTransform: 'none', letterSpacing: 0 }}>erkannt</span>}
            </label>
            <input data-testid={`draft-field-${f.key}-${vehicle.id}`} type={f.type || 'text'} min={f.min} max={f.max} value={form[f.key]} placeholder={f.ph}
              onChange={(e) => setField(f.key, e.target.value)} style={inp(f.key)} />
            {errors[f.key] && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 3 }}>{errors[f.key]}</div>}
            {!errors[f.key] && f.hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{f.hint}</div>}
          </div>
        ))}
      </div>
      {/* Schritt 3: bestätigen */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <button type="button" onClick={confirm} disabled={busy || !draft.ready} data-testid={`draft-confirm-${vehicle.id}`}
          style={{ padding: '10px 20px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, opacity: (busy || !draft.ready) ? 0.5 : 1 }}>
          {busy ? '…' : 'Bestätigen und zur Freigabe'}
        </button>
        {!draft.ready && <span style={{ fontSize: 12, color: '#6b7280' }}>Beide Dokumente hochladen, dann bestätigen.</span>}
      </div>
    </div>
  );
}
